import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  DOCUMENT,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../i18n/language.service';
import { FormsModule } from '@angular/forms';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Calendar, CalendarModule } from 'primeng/calendar';
import { SidebarModule } from 'primeng/sidebar';
import { Subscription } from 'rxjs';
import {
  AdminService,
  AdminUsersManagementService,
  AdminDeletedGroupListItemDto,
  AdminGroupListSportDto,
  AdminDeletedActivityListItemDto,
  ListDeletedActivitiesRequestParams,
  ListDeletedGroupsRequestParams,
} from '@smatchy/sdk';

import { formatDisplayDate, fromIsoDate, toDeletedRangeIso, toIsoDate } from '../../utils/management-date.util';
import { formatDurationFromNumber, formatTime, formatWeekday } from '../../utils/date.util';
import { getManagementTableLazyState } from '../../utils/management-table-state.util';
import { ManagementListDebounce } from '../../utils/management-list-debounce.util';
import { ReadonlyListsComponent } from '../../components/readonly-lists/readonly-lists.component';
import {
  GroupsManagementApiService,
  GroupManagementDetail,
  GroupActivityStatusTab,
} from '../../../services/groups-management/groups-management-api.service';
import { ActivitiesManagementApiService } from '../../../services/activities-management/activities-management-api.service';
import { ActivityPreviewStateService } from '../../../services/activities-management/activity-preview-state.service';
import {
  EditGroupActivity,
  EditGroupDraft,
  EditGroupMember,
  EditGroupSubgroup,
  ActivityStatusCounts,
  SportTypeConfig,
} from '../../models/groups-management.model';
import { getSportIconPath } from '../../utils/management-sport.util';
import { DEFAULT_GROUP_COVER_URL, EDIT_LIST_PREVIEW_COUNT } from '../../constants/management-ui.constants';
import { canTogglePreviewList, getPreviewItems, getPreviewMoreCount } from '../../utils/group-draft.util';
import { theBodyOf } from '../../utils/what-the-admin-api-returns';
import { ManagementViewportScrollService } from '../../../services/shared/management-viewport-scroll.service';
import { UsersManagementStoreService } from '../../../services/users-management/users-management.store.service';
import { UsersManagementUserAvatarComponent } from '../users-management/users-management-user-avatar.component';
import { getUserTypeIconPath } from '../users-management/users-management-user-type';
import { AdminDeletedUserListItemDto } from '@smatchy/sdk';
import {
  DeletedUsersByRoleFilter,
  getDeletedByDisplayName,
  matchesDeletedUsersByRole,
} from '../../utils/deleted-user-filter.util';

type DeletedUsersTableSortField =
  | 'name'
  | 'email'
  | 'createdAt'
  | 'userType'
  | 'status'
  | 'lastLoginDate'
  | 'pushNotification';

/**
 * One open subgroup drawer. The stack was typed inline, so the twelve helpers
 * that read a frame had nothing to refer to and each took `any` instead.
 */
/**
 * What the API really sends for a sport, which the generated client predates.
 *
 * groups.repository.mongo.ts fills nameFr and nameEn at all three places that
 * assemble this object, and the API DTO declares both as optional. The client
 * under libs/api-interfaces/src/lib/client-generated knows only id, name and
 * icon, so reading the localised names needed an assertion and the assertion
 * removed every other check with it.
 *
 * Declared here rather than asserted: the day the client is regenerated this
 * narrows to the real type instead of silently agreeing with anything.
 */
/**
 * The columns each table can be sorted by.
 *
 * getManagementTableLazyState is generic over this, and both predicates said
 * `field is any`, so the generic inferred any and every value that came out
 * of it had to be asserted back into place. Naming the columns restores the
 * generic the helper was written for, and the assertions go with it.
 */
type DeletedActivitiesSortField = 'activity' | 'group' | 'date' | 'status' | 'participants' | 'deletedAt';

type DeletedGroupsSortField =
  | 'name'
  | 'ownerName'
  | 'sportName'
  | 'totalSubgroups'
  | 'totalMembers'
  | 'totalActivities'
  | 'deletedAt';

type SportWithLocalisedNames = AdminGroupListSportDto & {
  nameFr?: string;
  nameEn?: string;
};

interface SubgroupDrawerFrame {
  subgroupDetail: EditGroupSubgroup;
  subgroupDetailData: GroupManagementDetail | null;
  subgroupsExpanded: boolean;
  membersExpanded: boolean;
  activitiesExpanded: boolean;
  visible: boolean;
  zIndex: number;
}

@Component({
  selector: 'bo-deleted-items',
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    TooltipModule,
    CalendarModule,
    FormsModule,
    SidebarModule,
    ReadonlyListsComponent,
    UsersManagementUserAvatarComponent,
  ],
  templateUrl: './deleted-items.component.html',
  styleUrls: ['./deleted-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeletedItemsComponent implements OnInit, OnDestroy {
  @ViewChild('groupsTable') private groupsTable?: Table;
  @ViewChild('deletedActivitiesTable') private deletedActivitiesTable?: Table;
  @ViewChild('deletedUsersTable') private deletedUsersTable?: Table;
  @ViewChild('dateRangeCalendar') dateRangeCalendar?: Calendar;
  @ViewChild('dateRangeField') dateRangeField?: ElementRef<HTMLElement>;

  private readonly adminService = inject(AdminService);
  private readonly adminUsersManagementService = inject(AdminUsersManagementService);
  private readonly usersStore = inject(UsersManagementStoreService);
  private readonly groupsApi = inject(GroupsManagementApiService);
  private readonly activitiesApi = inject(ActivitiesManagementApiService);
  private readonly viewportScroll = inject(ManagementViewportScrollService);
  readonly activityPreview = inject(ActivityPreviewStateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly language = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);

  private modalViewportLocked = false;
  private modalScrollPosition = 0;
  private readonly modalScrollKeys = new Set(['ArrowDown', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp', ' ']);
  private outsideDrawerDismissListenerAttached = false;

  private readonly preventBackgroundPointerScroll = (event: Event) => {
    if (!this.isAnyModalVisible()) {
      return;
    }
    if (this.viewportScroll.isDrawerScrollEvent(event, false)) {
      return;
    }
    event.preventDefault();
  };

  private readonly preventBackgroundKeyboardScroll = (event: KeyboardEvent) => {
    if (this.isAnyModalVisible() && this.modalScrollKeys.has(event.key)) {
      const target = event.target as Element | null;
      if (!target?.closest('.edit-drawer-body')) {
        event.preventDefault();
      }
    }
  };

  private readonly onOutsideDrawerPointerDown = (event: Event) => {
    if (!this.isAnyModalVisible()) {
      return;
    }
    if (event instanceof PointerEvent && event.button !== 0) {
      return;
    }

    // Check if click is on any drawer - if so, don't close
    const target = event.target as Element;
    const inSubgroupDrawer = target?.closest('[data-groups-drawer-body="subgroup"]');
    const inViewDrawer = target?.closest('[data-groups-drawer-body="group"]');
    const inActivityDrawer = target?.closest('[data-groups-drawer-body="activity"]');

    if (inSubgroupDrawer || inViewDrawer || inActivityDrawer) {
      return;
    }

    if (this.activityDetailVisible) {
      this.closeActivityDetail();
    } else if (this.subgroupDrawerStack.length > 0) {
      this.closeSubgroupDrawer();
    } else if (this.viewDrawerVisible) {
      this.closeViewDrawer();
    } else if (this.subgroupDrawerVisible) {
      this.subgroupDrawerVisible = false;
      this.unlockViewport();
      this.detachOutsideDismissListener();
      this.subgroupDetail = null;
      this.subgroupDetailData = null;
      this.cdr.markForCheck();
    }

    event.preventDefault();
    event.stopPropagation();
  };

  activeTab: 'groups' | 'users' | 'activities' = 'groups';

  // Counts for tabs
  counts = {
    groups: 0,
    users: 0,
    activities: 0,
  };

  // Group table data
  groups: AdminDeletedGroupListItemDto[] = [];
  totalRecords = 0;
  tableFirst = 0;
  tableRows = 10;
  tableLoading = false;
  tableSortField?: DeletedGroupsSortField;
  tableSortOrder?: 'asc' | 'desc';

  get tableSortOrderValue(): number {
    return this.tableSortOrder === 'asc' ? 1 : this.tableSortOrder === 'desc' ? -1 : 0;
  }

  // Filters
  searchQuery = '';
  showFilter = false;
  /**
   * The API accepts these three and nothing else. Declared as a bare string,
   * the screen could send anything and the request would fail at the edge.
   */
  selectedDeletedBy: 'admin' | 'user' | 'all' = 'all';
  selectedStartDate = '';
  selectedEndDate = '';
  dateRangeSelection: Date[] | undefined;
  dateRangeCalendarPanelStyle: Record<string, string> = {};

  // Activities tab date range (separate from groups)
  activitiesDateRangeSelection: Date[] | undefined;
  activitiesSelectedStartDate = '';
  activitiesSelectedEndDate = '';

  get deletedByOptions(): { label: string; value: string }[] {
    return [
      { label: this.translate.instant('financePages.obFilterAll'), value: 'all' },
      { label: 'Smatchy', value: 'admin' },
      { label: this.translate.instant('financePages.diUser'), value: 'user' },
    ];
  }

  // View Drawer State
  viewDrawerVisible = false;
  selectedGroupDetail: GroupManagementDetail | null = null;
  selectedGroupRow: AdminDeletedGroupListItemDto | null = null;
  groupDetailLoading = false;
  groupDrawerStack: {
    selectedGroupRow: AdminDeletedGroupListItemDto | null;
    selectedGroupDetail: GroupManagementDetail | null;
    membersExpanded: boolean;
    subgroupsExpanded: boolean;
    activitiesExpanded: boolean;
    activeActivityStatus: GroupActivityStatusTab;
  }[] = [];

  subgroupDrawerVisible = false;
  subgroupDrawerStack: SubgroupDrawerFrame[] = [];
  subgroupDetail: EditGroupSubgroup | null = null;
  subgroupDetailCoverFailed = false;
  subgroupMembersExpanded = false;
  subgroupSubgroupsExpanded = false;
  subgroupActivitiesExpanded = false;
  subgroupDetailData: GroupManagementDetail | null = null;
  skipSubgroupDrawerEnterAnimation = false;
  subgroupDrawerClosing = false;

  activityDetailVisible = false;
  activityDetail: EditGroupActivity | null = null;
  activityDetailLevel = 'all';
  activityDetailCoverFailed = false;
  activityDetailParticipantsExpanded = false;
  activityDetailParticipantsLoading = false;
  activityDetailOpenedFromGroup = false;

  membersExpanded = false;
  subgroupsExpanded = false;
  activitiesExpanded = false;
  activeActivityStatus: GroupActivityStatusTab = 'incoming';
  sportTypeConfigs: SportTypeConfig[] = [];
  defaultCoverPhotoUrl = DEFAULT_GROUP_COVER_URL;
  editListPreviewCount = EDIT_LIST_PREVIEW_COUNT;
  private readonly subgroupListPreviewCount = 2;
  private readonly activityListPreviewCount = 2;

  // View drawer callbacks
  readonly sportIconPath = (sport: string) => getSportIconPath(sport, this.sportTypeConfigs);
  readonly activityLevelLabel = (activity: EditGroupActivity) => {
    const level = activity.level?.trim().toLowerCase();
    return !level || level === 'all' || level === 'all levels'
      ? 'All levels'
      : `Level ${level.match(/\d+/)?.[0] ?? level}`;
  };
  readonly apiActivityTypeLabel = (activity: EditGroupActivity) =>
    (activity.activityType ?? '').replace(/\s+-\s+/g, ' - ');

  private readonly searchReload = new ManagementListDebounce();
  private readonly activitiesSearchReload = new ManagementListDebounce();
  private readonly usersSearchReload = new ManagementListDebounce();
  private requestSubscription: Subscription | null = null;
  private lastPageEventKey = '';
  private shouldIgnoreInitialLazyLoad = false;
  private readonly pageStateStorageKey = 'bo:deleted-activities:list:state';

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  loadDeletedItemsKpi() {
    this.adminService.getDeletedItemsKpi().subscribe({
      next: (res) => {
        const value = theBodyOf(res);
        this.counts.groups = value?.totalGroups ?? 0;
        this.counts.activities = value?.totalActivities ?? 0;
        this.counts.users = value?.totalUsers ?? 0;
        this.cdr.markForCheck();
      },
      error: () => {
        // Keep default counts (0) on error
        this.cdr.markForCheck();
      },
    });
  }

  ngOnInit() {
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck());
    this.shouldIgnoreInitialLazyLoad = true;
    this.document.documentElement.classList.add('groups-page-scroll-stable');
    this.loadDeletedItemsKpi();
    this.loadData();
    this.restoreActivitiesPageState();
    this.groupsApi.getSportTypes().subscribe((sportTypes) => {
      this.sportTypeConfigs = sportTypes;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy() {
    this.document.documentElement.classList.remove('groups-page-scroll-stable');
    this.searchReload.clear();
    this.activitiesSearchReload.clear();
    this.usersSearchReload.clear();
    this.requestSubscription?.unsubscribe();
    this.unlockViewport();
    this.detachOutsideDismissListener();
  }

  // Set active tab
  setActiveTab(tab: 'groups' | 'users' | 'activities') {
    this.activeTab = tab;
    this.tableFirst = 0;

    // Load data for the active tab
    if (tab === 'groups') {
      this.loadData();
    } else if (tab === 'users') {
      this.loadDeletedUsers();
    } else if (tab === 'activities') {
      this.loadDeletedActivities();
    }
  }

  deletedUsers: AdminDeletedUserListItemDto[] = [];
  deletedUsersTotal = 0;
  usersTableFirst = 0;
  usersTableRows = 10;
  usersTableLoading = false;
  usersTableSortField?: DeletedUsersTableSortField;
  usersTableSortOrder?: 'asc' | 'desc';
  usersSearchQuery = '';
  usersSelectedDeletedBy = 'all';
  usersDateRangeSelection: Date[] | undefined;
  usersSelectedStartDate = '';
  usersSelectedEndDate = '';
  private deletedUsersFilteredCache: AdminDeletedUserListItemDto[] = [];
  private deletedUsersFilterCacheKey = '';
  getUserTypeIconPath = getUserTypeIconPath;
  getDeletedByDisplayName = getDeletedByDisplayName;

  get usersTableSortOrderValue(): number {
    return this.usersTableSortOrder === 'asc' ? 1 : this.usersTableSortOrder === 'desc' ? -1 : 0;
  }

  get hasActiveUserFilters(): boolean {
    return (
      !!this.usersSearchQuery?.trim() ||
      this.usersSelectedDeletedBy !== 'all' ||
      !!this.usersSelectedStartDate ||
      !!this.usersSelectedEndDate
    );
  }

  get usersPaginationReportText(): string {
    const first = this.deletedUsersTotal ? this.usersTableFirst + 1 : 0;
    const last = this.deletedUsersTotal
      ? Math.min(this.usersTableFirst + this.usersTableRows, this.deletedUsersTotal)
      : 0;
    return `Showing ${first} to ${last} of ${this.deletedUsersTotal} results`;
  }

  loadDeletedUsers(showLoading = true) {
    if (this.activeTab !== 'users') {
      return;
    }

    const cacheKey = this.buildDeletedUsersFilterCacheKey();
    const useClientDeletedByFilter = this.usersSelectedDeletedBy !== 'all';
    if (
      useClientDeletedByFilter &&
      cacheKey === this.deletedUsersFilterCacheKey &&
      this.deletedUsersFilteredCache.length
    ) {
      this.applyDeletedUsersPageSlice();
      return;
    }

    if (showLoading) {
      this.usersTableLoading = true;
      this.cdr.detectChanges();
    }

    this.requestSubscription?.unsubscribe();
    const serverFilters = this.buildDeletedUsersServerFilters();

    if (useClientDeletedByFilter) {
      this.requestSubscription = this.usersStore.listAllDeletedUsers(serverFilters).subscribe({
        next: (users) => {
          this.deletedUsersFilteredCache = users.filter((user) =>
            matchesDeletedUsersByRole(user, this.usersSelectedDeletedBy as DeletedUsersByRoleFilter),
          );
          this.deletedUsersFilterCacheKey = cacheKey;
          this.applyDeletedUsersPageSlice();
        },
        error: () => {
          this.deletedUsersFilteredCache = [];
          this.deletedUsersFilterCacheKey = '';
          this.deletedUsers = [];
          this.deletedUsersTotal = 0;
          this.usersTableLoading = false;
          this.cdr.detectChanges();
        },
      });
      return;
    }

    this.deletedUsersFilteredCache = [];
    this.deletedUsersFilterCacheKey = '';
    const page = Math.floor(this.usersTableFirst / this.usersTableRows) + 1;

    this.requestSubscription = this.usersStore
      .listDeletedUsers({
        ...serverFilters,
        page,
        pageSize: this.usersTableRows,
      })
      .subscribe({
        next: (res) => {
          this.deletedUsers = res?.users ?? [];
          this.deletedUsersTotal = res?.totalUsers ?? 0;
          this.usersTableLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.deletedUsers = [];
          this.deletedUsersTotal = 0;
          this.usersTableLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private buildDeletedUsersServerFilters() {
    return {
      search: this.usersSearchQuery?.trim() || undefined,
      ...toDeletedRangeIso(this.usersSelectedStartDate || undefined, this.usersSelectedEndDate || undefined),
      sortBy: this.usersTableSortField,
      sortOrder: this.usersTableSortOrder,
    };
  }

  private buildDeletedUsersFilterCacheKey(): string {
    const serverFilters = this.buildDeletedUsersServerFilters();
    return JSON.stringify({
      ...serverFilters,
      deletedBy: this.usersSelectedDeletedBy,
    });
  }

  private applyDeletedUsersPageSlice() {
    const start = this.usersTableFirst;
    const end = start + this.usersTableRows;
    this.deletedUsers = this.deletedUsersFilteredCache.slice(start, end);
    this.deletedUsersTotal = this.deletedUsersFilteredCache.length;
    this.usersTableLoading = false;
    this.cdr.detectChanges();
  }

  onUsersSearchChanged() {
    this.usersSearchReload.schedule(() => {
      this.usersTableFirst = 0;
      this.loadDeletedUsers();
    });
  }

  onUsersDateRangeSelected() {
    const [startDate, endDate] = this.usersDateRangeSelection ?? [];
    this.usersSelectedStartDate = startDate ? toIsoDate(startDate) : '';
    this.usersSelectedEndDate = endDate ? toIsoDate(endDate) : this.usersSelectedStartDate;
    this.usersTableFirst = 0;
    this.loadDeletedUsers();
  }

  getUsersDateRangeStartLabel(): string {
    const [startDate] = this.usersDateRangeSelection ?? [];
    return startDate ? formatDisplayDate(startDate) : 'Start date';
  }

  getUsersDateRangeEndLabel(): string {
    const endDate = this.usersDateRangeSelection?.[1];
    return endDate ? formatDisplayDate(endDate) : 'End date';
  }

  hasUsersDateRangeStart(): boolean {
    return this.usersDateRangeSelection?.[0] instanceof Date;
  }

  hasUsersDateRangeEnd(): boolean {
    return this.usersDateRangeSelection?.[1] instanceof Date;
  }

  onUsersFilterChanged() {
    this.usersTableFirst = 0;
    this.loadDeletedUsers();
  }

  clearUsersFilter() {
    this.usersDateRangeSelection = undefined;
    this.usersSelectedStartDate = '';
    this.usersSelectedEndDate = '';
    this.usersSearchQuery = '';
    this.usersSelectedDeletedBy = 'all';
    this.deletedUsersFilteredCache = [];
    this.deletedUsersFilterCacheKey = '';
    this.usersTableFirst = 0;
    this.loadDeletedUsers();
  }

  onUsersTableLazyLoad(event: TableLazyLoadEvent) {
    const isUsersSortField = (field?: string | string[] | null): field is DeletedUsersTableSortField => {
      const value = typeof field === 'string' ? field : field?.[0];
      return (
        !!value &&
        ['name', 'email', 'createdAt', 'userType', 'status', 'lastLoginDate', 'pushNotification'].includes(value)
      );
    };

    const tableState = getManagementTableLazyState(event, this.usersTableRows, isUsersSortField);
    this.usersTableFirst = tableState.first;
    this.usersTableRows = tableState.rows;
    const sortState = this.getNextUsersSortState(tableState.sortField, tableState.sortOrder);
    this.usersTableSortField = sortState.sortField;
    this.usersTableSortOrder = sortState.sortOrder;
    if (sortState.cleared) {
      this.clearTableSort(this.deletedUsersTable);
    }
    this.loadDeletedUsers();
  }

  private getNextUsersSortState(
    sortField?: string,
    sortOrder?: 'asc' | 'desc',
  ): {
    sortField?: DeletedUsersTableSortField;
    sortOrder?: 'asc' | 'desc';
    cleared?: boolean;
  } {
    const shouldClearSort =
      sortField === this.usersTableSortField && this.usersTableSortOrder === 'desc' && sortOrder === 'asc';
    if (shouldClearSort) {
      return { cleared: true };
    }
    return {
      sortField: sortField as DeletedUsersTableSortField,
      sortOrder,
    };
  }

  // Deleted Activities table state
  deletedActivities: EditGroupActivity[] = [];
  deletedActivitiesTotal = 0;
  deletedTableFirst = 0;
  deletedTableRows = 10;
  deletedTableLoading = false;
  deletedTableSortField?: DeletedActivitiesSortField;
  deletedTableSortOrder?: 'asc' | 'desc';
  activitiesSearchQuery = '';
  /** Exact sport identifier, empty meaning every sport. */
  activitiesSportId = '';
  /** Free text matched against the activity address, not a city name. */
  activitiesLocation = '';

  /** Sport list for the filter, with an entry that clears it. */
  get activitiesSportOptions(): { label: string; value: string }[] {
    return [
      { label: this.translate.instant('financePages.diAllSports'), value: '' },
      ...this.sportTypeConfigs.map(({ label, value }) => ({ label, value })),
    ];
  }

  get deletedTableSortOrderValue(): number {
    return this.deletedTableSortOrder === 'asc' ? 1 : this.deletedTableSortOrder === 'desc' ? -1 : 0;
  }

  // Load data for the active tab (Groups tab)
  loadData(showLoading = true) {
    if (this.activeTab !== 'groups') {
      return;
    }

    if (showLoading) {
      this.tableLoading = true;
      this.cdr.detectChanges();
    }

    this.requestSubscription?.unsubscribe();

    const page = Math.floor(this.tableFirst / this.tableRows) + 1;
    const limit = this.tableRows;

    const params: ListDeletedGroupsRequestParams = {
      page,
      limit,
      search: this.searchQuery?.trim() || undefined,
      deletedByRole: this.selectedDeletedBy !== 'all' ? this.selectedDeletedBy : undefined,
      ...toDeletedRangeIso(this.selectedStartDate || undefined, this.selectedEndDate || undefined),
      sortBy: this.tableSortField || undefined,
      sortOrder: this.tableSortOrder || undefined,
    };

    this.requestSubscription = this.adminService.listDeletedGroups(params).subscribe({
      next: (res) => {
        const body = theBodyOf(res);
        this.groups = body?.data ?? [];
        this.totalRecords = body?.total ?? 0;
        this.tableLoading = false;

        this.cdr.detectChanges();
      },
      error: (error) => {
        this.groups = [];
        this.totalRecords = 0;
        this.tableLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  onSearchChanged() {
    this.searchReload.schedule(() => {
      this.tableFirst = 0;
      this.loadData();
    });
  }

  onActivitiesSearchChanged() {
    this.activitiesSearchReload.schedule(() => {
      this.deletedTableFirst = 0;
      this.loadDeletedActivities();
    });
  }

  preventRowTextActivation(event: PointerEvent) {
    const t = event.target as HTMLElement | null;
    if (!t) return;
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
  }

  openActivityFromList(activity: EditGroupActivity, syncUrl = true) {
    this.onActivityOpened(activity);
    this.persistActivitiesPageState();
    this.persistModalSession();
  }

  private persistActivitiesPageState() {
    try {
      const state = {
        first: this.deletedTableFirst,
        rows: this.deletedTableRows,
        sortField: this.deletedTableSortField,
        sortOrder: this.deletedTableSortOrder,
        search: this.activitiesSearchQuery,
      };
      sessionStorage.setItem(this.pageStateStorageKey, JSON.stringify(state));
    } catch {
      // ignore
    }
  }

  private restoreActivitiesPageState() {
    try {
      const raw = sessionStorage.getItem(this.pageStateStorageKey);
      if (!raw) return;
      const state = JSON.parse(raw);
      this.deletedTableFirst = state.first ?? this.deletedTableFirst;
      this.deletedTableRows = state.rows ?? this.deletedTableRows;
      this.deletedTableSortField = state.sortField ?? this.deletedTableSortField;
      this.deletedTableSortOrder = state.sortOrder ?? this.deletedTableSortOrder;
      this.activitiesSearchQuery = state.search ?? this.activitiesSearchQuery;
    } catch {
      // ignore
    }
  }

  private persistModalSession() {
    try {
      const modal = { open: this.activityDetailVisible };
      sessionStorage.setItem('bo:deleted-activities:modal', JSON.stringify(modal));
    } catch {
      // Le stockage de session peut être refusé (navigation privée,
      // réglages du navigateur). Perdre cet état d'affichage est sans gravité.
    }
  }

  // Load deleted activities list
  loadDeletedActivities(showLoading = true) {
    if (showLoading) {
      this.deletedTableLoading = true;
      this.cdr.detectChanges();
    }

    const page = Math.floor(this.deletedTableFirst / this.deletedTableRows) + 1;
    const limit = this.deletedTableRows;

    const params: ListDeletedActivitiesRequestParams = {
      page,
      limit,
      search: this.activitiesSearchQuery?.trim() || undefined,
      sportId: this.activitiesSportId || undefined,
      location: this.activitiesLocation?.trim() || undefined,
      sortBy: this.deletedTableSortField || undefined,
      sortOrder: this.deletedTableSortOrder || undefined,
      ...toDeletedRangeIso(this.activitiesSelectedStartDate || undefined, this.activitiesSelectedEndDate || undefined),
    };

    this.requestSubscription?.unsubscribe();
    this.requestSubscription = this.adminService.listDeletedActivities(params).subscribe({
      next: (res) => {
        const value = theBodyOf(res);
        const items = value?.data ?? [];
        this.deletedActivities = items.map((it) => this.mapApiDeletedActivity(it));
        this.deletedActivitiesTotal = value?.total ?? (items.length || 0);
        this.deletedTableLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.deletedActivities = [];
        this.deletedActivitiesTotal = 0;
        this.deletedTableLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  onActivitiesTableLazyLoad(event: TableLazyLoadEvent) {
    if (this.shouldIgnoreInitialLazyLoad) {
      this.shouldIgnoreInitialLazyLoad = false;
      return;
    }

    const isActivitiesSortField = (field?: string | string[] | null): field is DeletedActivitiesSortField => {
      return (
        !!field &&
        ['activity', 'group', 'date', 'status', 'participants', 'deletedAt'].includes(
          typeof field === 'string' ? field : field[0],
        )
      );
    };

    const tableState = getManagementTableLazyState(event, this.deletedTableRows, isActivitiesSortField);
    this.deletedTableFirst = tableState.first;
    this.deletedTableRows = tableState.rows;
    const sortState = this.getNextSortStateActivities(tableState.sortField, tableState.sortOrder);
    this.deletedTableSortField = sortState.sortField;
    this.deletedTableSortOrder = sortState.sortOrder;
    this.persistActivitiesPageState();
    this.loadDeletedActivities();
  }

  private getNextSortStateActivities(
    sortField?: DeletedActivitiesSortField,
    sortOrder?: 'asc' | 'desc',
  ): { sortField?: DeletedActivitiesSortField; sortOrder?: 'asc' | 'desc'; cleared?: boolean } {
    const shouldClearSort =
      sortField === this.deletedTableSortField && this.deletedTableSortOrder === 'desc' && sortOrder === 'asc';

    if (shouldClearSort) {
      return { cleared: true };
    }

    return { sortField, sortOrder };
  }

  /**
   * One row of the deleted activities list, as the API really sends it.
   *
   * EIGHT FALLBACK PATHS WERE REMOVED, and they were removed against the API
   * DTO rather than against the generated client, which this branch has shown
   * can be stale. list-admin-activities.dto.ts declares exactly four fields on
   * the activity: name, ownerName, ownerId and thumbnailUrl. So activity.date,
   * activity.time, activity.owner, activity.participantDetails,
   * activity.participants, item.participantDetails, item.participants and
   * item.participantsList never held anything.
   *
   * The visible consequence, which is none: the date already came from the
   * schedule, and participantDetails was always undefined here. The screen
   * loads participants with a separate call when a row is opened.
   */
  private mapApiDeletedActivity(item: AdminDeletedActivityListItemDto): EditGroupActivity {
    const activity = item?.activity;
    const group = item?.group;
    const schedule = item?.schedule;
    const sport = item?.sport;
    const deletedBy = item?.deletedBy ?? null;

    const startDate = schedule?.startDate ? new Date(schedule.startDate) : undefined;
    const endDate = schedule?.endDate ? new Date(schedule.endDate) : undefined;

    return {
      id: item?.id,
      name: activity?.name ?? '',
      host: activity?.ownerName ?? '',
      thumbnailUrl: activity?.thumbnailUrl ?? '',
      groupId: group?.id,
      groupName: group?.name ?? '',
      // Required by EditGroupActivity and never set: `as unknown as` hid it,
      // so every mapped row promised a string and carried undefined.
      location: item?.locationName ?? '',
      date: startDate ? startDate.toISOString() : '',
      time: startDate ? startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
      startDateIso: startDate ? startDate.toISOString() : undefined,
      endDateIso: endDate ? endDate.toISOString() : undefined,
      recurrence: schedule?.recurrence ?? 'once',
      schedule,
      sport: sport?.name ?? '',
      sportIcon: sport?.icon ?? '',
      status: item?.status ?? '',
      participants: item?.participantsCount ?? 0,
      participantDetails: undefined,
      type: item?.type ?? '',
      deletedBy: deletedBy ? { id: deletedBy.id, name: deletedBy.name, deletedAt: deletedBy.deletedAt } : undefined,
    };
  }

  onFilterChanged() {
    if (this.activeTab === 'groups') {
      this.tableFirst = 0;
      this.loadData();
    } else if (this.activeTab === 'users') {
      this.usersTableFirst = 0;
      this.loadDeletedUsers();
    } else if (this.activeTab === 'activities') {
      this.deletedTableFirst = 0;
      this.loadDeletedActivities();
    }
  }

  toggleFilter() {
    this.showFilter = !this.showFilter;
  }

  clearFilter() {
    this.dateRangeSelection = undefined;
    this.selectedStartDate = '';
    this.selectedEndDate = '';
    this.selectedDeletedBy = 'all';
    this.searchQuery = '';
    this.tableFirst = 0;
    this.loadData();
  }

  clearActivitiesFilter() {
    this.activitiesDateRangeSelection = undefined;
    this.activitiesSelectedStartDate = '';
    this.activitiesSelectedEndDate = '';
    this.activitiesSearchQuery = '';
    this.activitiesSportId = '';
    this.activitiesLocation = '';
    this.deletedTableFirst = 0;
    this.loadDeletedActivities();
  }

  // Date picker handlers
  toggleDateRangeCalendar(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.dateRangeCalendar?.overlayVisible) {
      this.dateRangeCalendar.hideOverlay();
      return;
    }

    this.syncDateRangeCalendarPanelWidth();
    this.dateRangeCalendar?.showOverlay();
  }

  onDateRangeCalendarShow() {
    requestAnimationFrame(() => this.alignDateRangeCalendarOverlay());
  }

  onDateRangeCalendarNavigate() {
    requestAnimationFrame(() => this.alignDateRangeCalendarOverlay());
  }

  onDateRangeSelected() {
    const [startDate, endDate] = this.dateRangeSelection ?? [];
    this.selectedStartDate = startDate ? toIsoDate(startDate) : '';
    this.selectedEndDate = endDate ? toIsoDate(endDate) : this.selectedStartDate;
    this.tableFirst = 0;
    this.loadData();
  }

  onActivitiesDateRangeSelected() {
    const [startDate, endDate] = this.activitiesDateRangeSelection ?? [];
    this.activitiesSelectedStartDate = startDate ? toIsoDate(startDate) : '';
    this.activitiesSelectedEndDate = endDate ? toIsoDate(endDate) : this.activitiesSelectedStartDate;
    this.deletedTableFirst = 0;
    this.loadDeletedActivities();
  }

  getDateRangeStartLabel(): string {
    const [startDate] = this.dateRangeSelection ?? [];
    return startDate ? formatDisplayDate(startDate) : 'Start date';
  }

  getDateRangeEndLabel(): string {
    const endDate = this.dateRangeSelection?.[1];
    return endDate ? formatDisplayDate(endDate) : 'End date';
  }

  hasDateRangeStart(): boolean {
    return this.dateRangeSelection?.[0] instanceof Date;
  }

  hasDateRangeEnd(): boolean {
    return this.dateRangeSelection?.[1] instanceof Date;
  }

  getActivitiesDateRangeStartLabel(): string {
    const [startDate] = this.activitiesDateRangeSelection ?? [];
    return startDate ? formatDisplayDate(startDate) : 'Start date';
  }

  getActivitiesDateRangeEndLabel(): string {
    const endDate = this.activitiesDateRangeSelection?.[1];
    return endDate ? formatDisplayDate(endDate) : 'End date';
  }

  hasActivitiesDateRangeStart(): boolean {
    return this.activitiesDateRangeSelection?.[0] instanceof Date;
  }

  hasActivitiesDateRangeEnd(): boolean {
    return this.activitiesDateRangeSelection?.[1] instanceof Date;
  }

  private syncDateRangeCalendarPanelWidth() {
    const width = this.dateRangeField?.nativeElement.getBoundingClientRect().width ?? 348;
    this.dateRangeCalendarPanelStyle = {
      width: `${Math.round(width)}px`,
      minWidth: `${Math.round(width)}px`,
    };
    this.cdr.markForCheck();
  }

  private alignDateRangeCalendarOverlay() {
    const trigger = this.dateRangeField?.nativeElement;
    const overlay = document.querySelector<HTMLElement>('.activity-date-calendar-popover');
    if (!trigger || !overlay) {
      return;
    }

    const gap = 5;
    const headerHeight = document.querySelector<HTMLElement>('.layout-topbar')?.offsetHeight ?? 80;
    const safeTop = headerHeight + gap;

    const triggerRect = trigger.getBoundingClientRect();
    const overlayHeight = overlay.offsetHeight;
    const overlayWidth = Math.round(triggerRect.width);

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top - safeTop;

    const openAbove = spaceBelow < overlayHeight + gap && spaceAbove >= overlayHeight + gap;

    const viewportTop = openAbove ? triggerRect.top - overlayHeight - gap : triggerRect.bottom + gap;

    overlay.classList.toggle('activity-date-calendar-popover--above', openAbove);
    overlay.style.left = `${Math.round(window.scrollX + triggerRect.left)}px`;
    overlay.style.top = `${Math.round(window.scrollY + viewportTop)}px`;
    overlay.style.width = `${overlayWidth}px`;
    overlay.style.minWidth = `${overlayWidth}px`;
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChanged() {
    if (this.dateRangeCalendar?.overlayVisible) {
      this.alignDateRangeCalendarOverlay();
    }
  }

  onTableLazyLoad(event: TableLazyLoadEvent) {
    if (this.shouldIgnoreInitialLazyLoad) {
      this.shouldIgnoreInitialLazyLoad = false;
      return;
    }

    const isGroupsSortField = (field?: string | string[] | null): field is DeletedGroupsSortField => {
      return (
        !!field &&
        ['name', 'ownerName', 'sportName', 'totalSubgroups', 'totalMembers', 'totalActivities', 'deletedAt'].includes(
          typeof field === 'string' ? field : field[0],
        )
      );
    };

    const tableState = getManagementTableLazyState(event, this.tableRows, isGroupsSortField);
    const pageEventKey = this.getTablePageEventKey(tableState.first, tableState.rows);
    const shouldSkipDuplicatePageLoad = !!this.lastPageEventKey && this.lastPageEventKey === pageEventKey;
    this.lastPageEventKey = '';
    this.tableFirst = tableState.first;
    this.tableRows = tableState.rows;
    const sortState = this.getNextSortState(tableState.sortField, tableState.sortOrder);
    this.tableSortField = sortState.sortField;
    this.tableSortOrder = sortState.sortOrder;
    if (sortState.cleared) {
      this.clearTableSort(this.groupsTable);
    }

    if (shouldSkipDuplicatePageLoad) {
      return;
    }

    this.loadData();
  }

  private getTablePageEventKey(first: number, rows: number): string {
    return `${first}-${rows}`;
  }

  private getNextSortState(
    sortField?: DeletedGroupsSortField,
    sortOrder?: 'asc' | 'desc',
  ): { sortField?: DeletedGroupsSortField; sortOrder?: 'asc' | 'desc'; cleared?: boolean } {
    const shouldClearSort = sortField === this.tableSortField && this.tableSortOrder === 'desc' && sortOrder === 'asc';

    if (shouldClearSort) {
      return { cleared: true };
    }

    return {
      sortField,
      sortOrder,
    };
  }

  private clearTableSort(table?: Table) {
    if (!table) {
      return;
    }

    table.sortField = null;
    table.sortOrder = 0;
    table.tableService.onSort(null);
  }

  getGroupLeadInitial(group: AdminDeletedGroupListItemDto): string {
    return (group.owner?.displayName ?? '').trim().charAt(0).toUpperCase() || '-';
  }

  getGroupLeadAvatarUrl(group: AdminDeletedGroupListItemDto): string | null {
    const avatarUrl = group.owner?.avatarUrl?.trim();
    return avatarUrl || null;
  }

  onGroupLeadAvatarError(group: AdminDeletedGroupListItemDto) {
    if (group.owner) {
      group.owner = { ...group.owner, avatarUrl: undefined };
      this.cdr.markForCheck();
    }
  }

  toSportIconSource(icon?: string): string | undefined {
    const trimmedIcon = icon?.trim();
    if (!trimmedIcon) {
      return undefined;
    }

    if (
      trimmedIcon.startsWith('http://') ||
      trimmedIcon.startsWith('https://') ||
      trimmedIcon.startsWith('data:') ||
      trimmedIcon.startsWith('assets/') ||
      trimmedIcon.startsWith('/assets/')
    ) {
      return trimmedIcon;
    }

    if (trimmedIcon.includes('/')) {
      return trimmedIcon;
    }

    return trimmedIcon.includes('.') ? `assets/icons/sport/${trimmedIcon}` : `assets/icons/sport/${trimmedIcon}.svg`;
  }

  getGroupSportName(group: AdminDeletedGroupListItemDto): string {
    const sport: Partial<SportWithLocalisedNames> = group?.sport ?? {};
    const french = this.language.current() === 'fr';
    return (french ? sport.nameFr || sport.nameEn : sport.nameEn || sport.nameFr) || sport.name || '';
  }

  getGroupSportIconSrc(group: AdminDeletedGroupListItemDto): string | undefined {
    return this.toSportIconSource(group.sport?.icon);
  }

  getPaginationFirst(): number {
    return this.totalRecords ? this.tableFirst + 1 : 0;
  }

  getPaginationLast(): number {
    return this.totalRecords ? Math.min(this.tableFirst + this.tableRows, this.totalRecords) : 0;
  }

  get paginationReportText(): string {
    return `Showing ${this.getPaginationFirst()} to ${this.getPaginationLast()} of ${this.totalRecords} results`;
  }

  get hasActiveFilters(): boolean {
    return !!this.searchQuery || this.selectedDeletedBy !== 'all' || !!this.selectedStartDate || !!this.selectedEndDate;
  }

  get hasActiveActivityFilters(): boolean {
    return (
      !!this.activitiesSearchQuery?.trim() || !!this.activitiesSelectedStartDate || !!this.activitiesSelectedEndDate
    );
  }

  isAnyModalVisible(): boolean {
    return this.viewDrawerVisible || this.subgroupDrawerVisible || this.activityDetailVisible;
  }

  viewGroupDetails(group: AdminDeletedGroupListItemDto) {
    this.selectedGroupRow = group;
    this.selectedGroupDetail = this.buildInitialDraft(group);
    this.viewDrawerVisible = true;
    this.activityDetailVisible = false;
    this.subgroupDrawerVisible = false;
    this.subgroupDetail = null;
    this.subgroupDetailData = null;
    this.subgroupDrawerStack = [];
    this.membersExpanded = false;
    this.subgroupsExpanded = false;
    this.activitiesExpanded = false;
    this.activeActivityStatus = 'incoming';
    this.groupDrawerStack = [];
    this.lockViewport();
    this.attachOutsideDismissListener();
    this.cdr.markForCheck();

    this.groupsApi.getDetail(group.id).subscribe({
      next: (detail) => {
        if (this.selectedGroupRow?.id !== group.id) {
          return;
        }
        this.selectedGroupDetail = detail;
        this.cdr.markForCheck();
      },
      error: () => {
        if (this.selectedGroupRow?.id === group.id) {
          this.cdr.markForCheck();
        }
      },
    });
  }

  private buildInitialDraft(group: AdminDeletedGroupListItemDto): GroupManagementDetail {
    return {
      draft: {
        id: group.id,
        name: group.name,
        createdOn: '',
        description: '',
        sport: group.sport?.name || '',
        coverPhotoUrl: '',
        // Required by EditGroupDraft and simply missed: the placeholder sets
        // every other field to empty, and `any` kept the compiler from saying so.
        websiteUrl: '',
      },
      membersTotal: group.totalMembers,
      subgroupsTotal: group.totalSubgroups,
      activitiesTotal: group.totalActivities,
      members: [],
      subgroups: [],
      activities: [],
      activityStatusCounts: this.getEmptyActivityStatusCounts(),
    };
  }

  closeViewDrawer() {
    if (this.groupDrawerStack.length > 0) {
      const previousFrame = this.groupDrawerStack.pop();
      if (previousFrame) {
        this.selectedGroupRow = previousFrame.selectedGroupRow;
        this.selectedGroupDetail = previousFrame.selectedGroupDetail;
        this.membersExpanded = previousFrame.membersExpanded;
        this.subgroupsExpanded = previousFrame.subgroupsExpanded;
        this.activitiesExpanded = previousFrame.activitiesExpanded;
        this.activeActivityStatus = previousFrame.activeActivityStatus;
        this.cdr.markForCheck();
        return;
      }
    }

    this.viewDrawerVisible = false;
    this.activityDetailVisible = false;
    this.unlockViewport();
    this.detachOutsideDismissListener();
    this.selectedGroupRow = null;
    this.selectedGroupDetail = null;
    this.activityDetail = null;
    this.groupDrawerStack = [];
    this.cdr.markForCheck();
  }

  closeNestedDrawers() {
    this.activityDetailVisible = false;
    this.activityDetail = null;

    this.subgroupDrawerStack.forEach((frame) => {
      frame.visible = false;
    });
    this.subgroupDrawerStack = [];

    this.subgroupDrawerVisible = false;
    this.viewDrawerVisible = false;
    this.unlockViewport();
    this.detachOutsideDismissListener();
    this.selectedGroupRow = null;
    this.selectedGroupDetail = null;
    this.subgroupDetail = null;
    this.subgroupDetailData = null;
    this.groupDrawerStack = [];
    this.cdr.markForCheck();
  }

  onViewDrawerHidden() {
    if (this.viewDrawerVisible) {
      return;
    }
    this.unlockViewport();
    this.detachOutsideDismissListener();
    this.selectedGroupRow = null;
    this.selectedGroupDetail = null;
    this.activityDetail = null;
    this.groupDrawerStack = [];
  }

  private lockViewport() {
    this.updateModalViewportLock();
  }

  private unlockViewport() {
    this.updateModalViewportLock();
  }

  onSubgroupOpened(subgroup: EditGroupSubgroup) {
    this.activityDetailVisible = false;
    this.activityDetail = null;

    const newFrame = {
      subgroupDetail: { ...subgroup },
      subgroupDetailData: null as GroupManagementDetail | null,
      subgroupsExpanded: false,
      membersExpanded: false,
      activitiesExpanded: false,
      visible: true,
      zIndex: 2000 + this.subgroupDrawerStack.length + 1,
    };
    this.subgroupDrawerStack.push(newFrame);
    this.subgroupDetail = newFrame.subgroupDetail;
    this.subgroupDrawerVisible = true;
    this.lockViewport();
    this.attachOutsideDismissListener();
    this.cdr.markForCheck();

    this.groupsApi.getDetail(subgroup.id).subscribe({
      next: (detail: GroupManagementDetail) => {
        const frameIndex = this.subgroupDrawerStack.length - 1;
        if (frameIndex >= 0) {
          this.subgroupDrawerStack[frameIndex].subgroupDetailData = detail;
          this.subgroupDrawerStack[frameIndex].subgroupDetail = {
            id: subgroup.id,
            name: detail.draft.name,
            members: detail.membersTotal ?? subgroup.members,
            activities: detail.activitiesTotal ?? subgroup.activities,
            thumbnailUrl: detail.draft.coverPhotoUrl ?? subgroup.thumbnailUrl,
            sport: detail.draft.sport ?? subgroup.sport,
            sportIcon: detail.draft.sportIcon ?? subgroup.sportIcon,
            location: subgroup.location,
            admin: subgroup.admin,
            description: detail.draft.description ?? subgroup.description,
            websiteUrl: detail.draft.websiteUrl ?? subgroup.websiteUrl,
          } as EditGroupSubgroup;
          this.subgroupDetail = this.subgroupDrawerStack[frameIndex].subgroupDetail;
          this.subgroupDetailData = detail;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.cdr.markForCheck();
      },
    });
  }

  onActivityOpened(activity: EditGroupActivity) {
    this.activityDetail = { ...activity };
    this.activityDetailLevel = this.activityPreview.getLevelValue(
      activity.level,
      activity.sport,
      this.sportTypeConfigs,
    );
    this.activityDetailCoverFailed = false;
    this.activityDetailParticipantsExpanded = false;
    this.activityDetailParticipantsLoading = false;
    this.activityDetailOpenedFromGroup = this.viewDrawerVisible;
    this.activityDetailVisible = true;
    this.lockViewport();
    this.attachOutsideDismissListener();
    this.cdr.markForCheck();

    this.activitiesApi.getDetail(activity.id).subscribe({
      next: (detail: EditGroupActivity) => {
        if (!this.activityDetailVisible) return;
        this.activityDetail = detail;
        this.activityDetailLevel = this.activityPreview.getLevelValue(
          detail.level,
          detail.sport,
          this.sportTypeConfigs,
        );
        this.cdr.markForCheck();
      },
      error: () => {
        this.cdr.markForCheck();
      },
    });
  }

  getActivityDetailCoverSrc(): string {
    if (this.activityDetailCoverFailed || !this.activityDetail?.thumbnailUrl?.trim()) {
      return DEFAULT_GROUP_COVER_URL;
    }
    return this.activityDetail.thumbnailUrl;
  }

  onActivityDetailCoverError() {
    this.activityDetailCoverFailed = true;
    this.cdr.markForCheck();
  }

  onActivityDetailOrganizerAvatarError() {
    if (this.activityDetail) {
      this.activityDetail = { ...this.activityDetail, hostAvatarUrl: undefined };
      this.cdr.markForCheck();
    }
  }

  onActivityDetailParticipantAvatarError(participant: EditGroupMember) {
    participant.avatarUrl = undefined;
  }

  getSubgroupCoverPhotoSrc(frame: SubgroupDrawerFrame): string {
    if (!frame?.subgroupDetail?.thumbnailUrl?.trim()) {
      return DEFAULT_GROUP_COVER_URL;
    }
    return frame.subgroupDetail.thumbnailUrl;
  }

  onSubgroupCoverPhotoError(frame: SubgroupDrawerFrame) {
    this.cdr.markForCheck();
  }

  getSubgroupVisibleMembers(frame: SubgroupDrawerFrame): EditGroupMember[] {
    if (!frame?.subgroupDetailData) return [];
    const members = frame.subgroupDetailData.members ?? [];
    if (frame.membersExpanded) {
      return members;
    }
    return members.slice(0, this.editListPreviewCount);
  }

  getSubgroupMembersMoreCount(frame: SubgroupDrawerFrame): number {
    if (!frame?.subgroupDetailData) return 0;
    const total = frame.subgroupDetailData.membersTotal ?? 0;
    if (frame.membersExpanded) {
      return 0;
    }
    return Math.max(total - this.editListPreviewCount, 0);
  }

  canToggleSubgroupMembersList(frame: SubgroupDrawerFrame): boolean {
    if (!frame?.subgroupDetailData) return false;
    const total = frame.subgroupDetailData.membersTotal ?? 0;
    if (frame.membersExpanded) {
      return (frame.subgroupDetailData.members?.length ?? 0) > this.editListPreviewCount;
    }
    return total > this.editListPreviewCount;
  }

  toggleSubgroupMembersExpanded(frame: SubgroupDrawerFrame) {
    frame.membersExpanded = !frame.membersExpanded;
    this.cdr.markForCheck();
  }

  getSubgroupVisibleSubgroups(frame: SubgroupDrawerFrame): EditGroupSubgroup[] {
    if (!frame?.subgroupDetailData) return [];
    const subgroups = frame.subgroupDetailData.subgroups ?? [];
    if (frame.subgroupsExpanded) {
      return subgroups;
    }
    return subgroups.slice(0, this.subgroupListPreviewCount);
  }

  getSubgroupSubgroupsMoreCount(frame: SubgroupDrawerFrame): number {
    if (!frame?.subgroupDetailData) return 0;
    const total = frame.subgroupDetailData.subgroupsTotal ?? 0;
    if (frame.subgroupsExpanded) {
      return 0;
    }
    return Math.max(total - this.subgroupListPreviewCount, 0);
  }

  canToggleSubgroupSubgroupsList(frame: SubgroupDrawerFrame): boolean {
    if (!frame?.subgroupDetailData) return false;
    const total = frame.subgroupDetailData.subgroupsTotal ?? 0;
    if (frame.subgroupsExpanded) {
      return (frame.subgroupDetailData.subgroups?.length ?? 0) > this.subgroupListPreviewCount;
    }
    return total > this.subgroupListPreviewCount;
  }

  toggleSubgroupSubgroupsExpanded(frame: SubgroupDrawerFrame) {
    frame.subgroupsExpanded = !frame.subgroupsExpanded;
    this.cdr.markForCheck();
  }

  getSubgroupVisibleActivities(frame: SubgroupDrawerFrame): EditGroupActivity[] {
    if (!frame?.subgroupDetailData) return [];
    return frame.subgroupDetailData.activities ?? [];
  }

  getSubgroupActivitiesMoreCount(frame: SubgroupDrawerFrame): number {
    if (!frame?.subgroupDetailData) return 0;
    const total = frame.subgroupDetailData.activitiesTotal ?? 0;
    if (frame.activitiesExpanded) {
      return 0;
    }
    return Math.max(total - this.activityListPreviewCount, 0);
  }

  canToggleSubgroupActivitiesList(frame: SubgroupDrawerFrame): boolean {
    if (!frame?.subgroupDetailData) return false;
    const total = frame.subgroupDetailData.activitiesTotal ?? 0;
    if (frame.activitiesExpanded) {
      return (frame.subgroupDetailData.activities?.length ?? 0) > this.activityListPreviewCount;
    }
    return total > this.activityListPreviewCount;
  }

  toggleSubgroupActivitiesExpanded(frame: SubgroupDrawerFrame, status: GroupActivityStatusTab) {
    frame.activitiesExpanded = !frame.activitiesExpanded;
    if (frame.activitiesExpanded && frame.subgroupDetailData) {
      const subgroupId = frame.subgroupDetail?.id;
      if (!subgroupId) {
        this.cdr.markForCheck();
        return;
      }
      const statusCounts: ActivityStatusCounts = frame.subgroupDetailData.activityStatusCounts ?? {
        incoming: 0,
        completed: 0,
        cancelled: 0,
      };
      const total = statusCounts[status] ?? 0;
      const alreadyForStatus = (frame.subgroupDetailData.activities ?? []).filter(
        (act: EditGroupActivity) => this.toActivityStatusCountKey(act.status) === status,
      );
      if (alreadyForStatus.length < total) {
        this.groupsApi.getActivities(subgroupId, 1, total, status).subscribe((section) => {
          if (frame.subgroupDetailData) {
            const others = (frame.subgroupDetailData.activities ?? []).filter(
              (act: EditGroupActivity) => this.toActivityStatusCountKey(act.status) !== status,
            );
            frame.subgroupDetailData.activities = [...others, ...section.data];
            this.cdr.markForCheck();
          }
        });
      }
    }
    this.cdr.markForCheck();
  }

  getActivityDetailParticipants(): EditGroupMember[] {
    if (!this.activityDetail) return [];
    const participants = this.activityDetail.participantDetails ?? [];
    if (this.activityDetailParticipantsExpanded) {
      return participants;
    }
    return participants.slice(0, 3);
  }

  canToggleActivityDetailParticipants(): boolean {
    if (!this.activityDetail) return false;
    const total = this.activityDetail.participants ?? 0;
    const loaded = this.activityDetail.participantDetails?.length ?? 0;
    if (this.activityDetailParticipantsExpanded) {
      return loaded > 3;
    }
    return total > 3;
  }

  getActivityDetailParticipantsMoreCount(): number {
    if (!this.activityDetail) return 0;
    const total = this.activityDetail.participants ?? 0;
    if (this.activityDetailParticipantsExpanded) {
      return 0;
    }
    return Math.max(total - 3, 0);
  }

  toggleActivityDetailParticipants() {
    if (this.activityDetailParticipantsExpanded) {
      this.activityDetailParticipantsExpanded = false;
      this.cdr.markForCheck();
      return;
    }
    const activity = this.activityDetail;
    if (!activity?.id || this.activityDetailParticipantsLoading) return;

    const loadedCount = activity.participantDetails?.length ?? 0;
    if (loadedCount >= activity.participants) {
      this.activityDetailParticipantsExpanded = true;
      this.cdr.markForCheck();
      return;
    }

    this.activityDetailParticipantsLoading = true;
    this.requestSubscription?.unsubscribe();
    this.requestSubscription = this.activitiesApi
      .getParticipants(activity.id, 1, Math.max(activity.participants, 10))
      .subscribe({
        next: (section) => {
          this.activityDetailParticipantsLoading = false;
          if (!this.activityDetail || this.activityDetail.id !== activity.id) return;
          this.activityDetail = {
            ...this.activityDetail,
            participantDetails: section.data,
            participants: section.total,
          };
          this.activityDetailParticipantsExpanded = true;
          this.cdr.markForCheck();
        },
        error: () => {
          this.activityDetailParticipantsLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  closeActivityDetail() {
    this.activityDetailVisible = false;
    this.activityDetail = null;
    if (!this.viewDrawerVisible && !this.subgroupDrawerVisible) {
      this.unlockViewport();
      this.detachOutsideDismissListener();
    }
    this.cdr.markForCheck();
  }

  closeSubgroupDrawer() {
    if (this.subgroupDrawerStack.length > 0) {
      this.subgroupDrawerStack.pop();
      if (this.subgroupDrawerStack.length > 0) {
        const previousFrame = this.subgroupDrawerStack[this.subgroupDrawerStack.length - 1];
        this.subgroupDetail = previousFrame.subgroupDetail;
        this.subgroupDetailData = previousFrame.subgroupDetailData;
        this.subgroupSubgroupsExpanded = previousFrame.subgroupsExpanded;
        this.subgroupMembersExpanded = previousFrame.membersExpanded;
        this.subgroupActivitiesExpanded = previousFrame.activitiesExpanded;
        this.subgroupDrawerVisible = true;
        this.cdr.markForCheck();
        return;
      }
    }

    this.subgroupDrawerVisible = false;

    if (!this.viewDrawerVisible) {
      this.unlockViewport();
      this.detachOutsideDismissListener();
    }
    this.subgroupDetail = null;
    this.subgroupDetailData = null;
    this.cdr.markForCheck();
  }

  onSubgroupDrawerHidden(index: number) {
    if (index >= 0 && index < this.subgroupDrawerStack.length) {
      this.subgroupDrawerStack.splice(index, 1);
      if (this.subgroupDrawerStack.length === 0) {
        this.subgroupDrawerVisible = false;
        this.unlockViewport();
        this.detachOutsideDismissListener();
      }
    }
    this.cdr.markForCheck();
  }

  private updateModalViewportLock(restoredScrollPosition?: number) {
    if (!this.isAnyModalVisible()) {
      this.unlockModalViewport();
      return;
    }

    const scrollY =
      restoredScrollPosition ??
      (this.modalViewportLocked ? this.modalScrollPosition : null) ??
      this.document.defaultView?.scrollY ??
      0;
    this.modalScrollPosition = scrollY;

    if (this.modalViewportLocked) {
      this.viewportScroll.updateModalBackgroundLock(this.modalScrollPosition);
      return;
    }

    this.viewportScroll.lockModalBackground(
      this.modalScrollPosition,
      this.preventBackgroundPointerScroll,
      this.preventBackgroundKeyboardScroll,
    );
    this.modalViewportLocked = true;
  }

  private unlockModalViewport() {
    if (!this.modalViewportLocked) {
      return;
    }

    this.viewportScroll.unlockModalBackground(
      this.modalScrollPosition,
      this.preventBackgroundPointerScroll,
      this.preventBackgroundKeyboardScroll,
    );
    this.modalViewportLocked = false;
  }

  private attachOutsideDismissListener() {
    if (this.outsideDrawerDismissListenerAttached) {
      return;
    }
    this.document.addEventListener('pointerdown', this.onOutsideDrawerPointerDown, true);
    this.outsideDrawerDismissListenerAttached = true;
  }

  private detachOutsideDismissListener() {
    if (!this.outsideDrawerDismissListenerAttached) {
      return;
    }
    this.document.removeEventListener('pointerdown', this.onOutsideDrawerPointerDown, true);
    this.outsideDrawerDismissListenerAttached = false;
  }

  getCoverPhotoSrc(): string {
    return this.selectedGroupDetail?.draft?.coverPhotoUrl || this.defaultCoverPhotoUrl;
  }

  onCoverPhotoError(event: Event) {
    const image = event.target as HTMLImageElement;
    if (!image.src.includes(this.defaultCoverPhotoUrl)) {
      image.src = this.defaultCoverPhotoUrl;
      this.cdr.markForCheck();
    }
  }

  hasDescription(desc?: string): boolean {
    return !!desc?.trim();
  }

  getDescriptionText(desc?: string): string {
    return desc?.trim() || 'No description provided';
  }

  hasWebsiteUrl(websiteUrl?: string): boolean {
    return !!websiteUrl?.trim();
  }

  getWebsiteUrlText(websiteUrl?: string): string {
    return websiteUrl?.trim() || 'No website link provided';
  }

  hasRouteUrl(routeUrl?: string): boolean {
    return !!routeUrl?.trim();
  }

  getRouteUrlText(routeUrl?: string): string {
    return routeUrl?.trim() || '';
  }

  getRouteUrlHref(routeUrl?: string): string | null {
    return routeUrl?.trim() || null;
  }

  getInitials(name?: string): string {
    if (!name) return '';
    return name.trim().charAt(0).toUpperCase();
  }

  getParticipantAvatarUrl(participant: EditGroupMember): string | null {
    return participant?.avatarUrl?.trim() || null;
  }

  onParticipantAvatarError(participant: EditGroupMember) {
    if (participant) participant.avatarUrl = undefined;
    this.cdr.markForCheck();
  }

  onActivityThumbError(activity: EditGroupActivity | null) {
    if (!activity) return;
    activity.thumbnailUrl = '';
    this.cdr.markForCheck();
  }

  getOrganizerAvatarUrl(activity: EditGroupActivity | null): string | null {
    return activity?.hostAvatarUrl?.trim() || null;
  }

  onOrganizerAvatarError(activity: EditGroupActivity | null) {
    if (activity) activity.hostAvatarUrl = undefined;
    this.cdr.markForCheck();
  }

  getVisibilityText(activity: EditGroupActivity | null): string {
    if (activity?.privacy === 'public') {
      return 'Everyone';
    }
    if (activity?.privacy === 'members_only') {
      return `Member of ${activity.groupName ?? ''}`.trim();
    }
    if (activity?.privacy === 'private') {
      return 'Private';
    }
    return 'Everyone';
  }

  getDraftSportIconPath(draft: EditGroupDraft | EditGroupSubgroup | EditGroupActivity | null): string | null {
    if (!draft?.sport) {
      return null;
    }
    return this.sportIconPath(draft.sport);
  }

  hasDraftSport(draft: EditGroupDraft | EditGroupSubgroup | EditGroupActivity | null): boolean {
    return !!draft?.sport;
  }

  getVisibleMembers(): EditGroupMember[] {
    const members = this.selectedGroupDetail?.members ?? [];
    return getPreviewItems(members, this.membersExpanded, this.editListPreviewCount);
  }

  getMembersMoreCount(): number {
    const total = this.selectedGroupDetail?.membersTotal ?? 0;
    return getPreviewMoreCount(total, this.membersExpanded, this.editListPreviewCount);
  }

  canToggleMembersList(): boolean {
    const total = this.selectedGroupDetail?.membersTotal ?? 0;
    return canTogglePreviewList(total, this.membersExpanded, this.editListPreviewCount);
  }

  toggleMembersExpanded() {
    this.membersExpanded = !this.membersExpanded;
    if (this.membersExpanded && this.selectedGroupDetail && this.selectedGroupRow) {
      const groupId = this.selectedGroupRow.id;
      const total = this.selectedGroupDetail.membersTotal;
      this.groupsApi.getMembers(groupId, 1, total).subscribe((section) => {
        if (this.selectedGroupRow?.id === groupId && this.selectedGroupDetail) {
          this.selectedGroupDetail.members = section.data;
          this.cdr.markForCheck();
        }
      });
    }
    this.cdr.markForCheck();
  }

  getVisibleSubgroups(): EditGroupSubgroup[] {
    const subgroups = this.selectedGroupDetail?.subgroups ?? [];
    return getPreviewItems(subgroups, this.subgroupsExpanded, this.subgroupListPreviewCount);
  }

  getSubgroupsMoreCount(): number {
    const total = this.selectedGroupDetail?.subgroupsTotal ?? 0;
    return getPreviewMoreCount(total, this.subgroupsExpanded, this.subgroupListPreviewCount);
  }

  canToggleSubgroupsList(): boolean {
    const total = this.selectedGroupDetail?.subgroupsTotal ?? 0;
    return canTogglePreviewList(total, this.subgroupsExpanded, this.subgroupListPreviewCount);
  }

  toggleSubgroupsExpanded() {
    this.subgroupsExpanded = !this.subgroupsExpanded;
    if (this.subgroupsExpanded && this.selectedGroupDetail && this.selectedGroupRow) {
      const groupId = this.selectedGroupRow.id;
      const total = this.selectedGroupDetail.subgroupsTotal;
      this.groupsApi.getSubgroups(groupId, 1, total).subscribe((section) => {
        if (this.selectedGroupRow?.id === groupId && this.selectedGroupDetail) {
          this.selectedGroupDetail.subgroups = section.data;
          this.cdr.markForCheck();
        }
      });
    }
    this.cdr.markForCheck();
  }

  getComputedActivities(): EditGroupActivity[] {
    return this.selectedGroupDetail?.activities ?? [];
  }

  getActivitiesMoreCount(): number {
    const total = this.getActivityListTotal();
    return getPreviewMoreCount(total, this.activitiesExpanded, this.activityListPreviewCount);
  }

  canToggleActivitiesList(): boolean {
    const total = this.getActivityListTotal();
    return canTogglePreviewList(total, this.activitiesExpanded, this.activityListPreviewCount);
  }

  getActivityGroup(activity: EditGroupActivity): string {
    const name = activity.groupName?.trim();
    return name ? name : '--';
  }

  getActivityStatus(activity: EditGroupActivity): 'incoming' | 'completed' | 'canceled' {
    return this.normalizeActivityStatus(activity.status);
  }

  private normalizeActivityStatus(status?: string): 'incoming' | 'completed' | 'canceled' {
    const value = (status ?? '').toString().trim().toLowerCase();
    switch (value) {
      case 'scheduled':
      case 'incoming':
        return 'incoming';
      case 'finished':
      case 'completed':
        return 'completed';
      case 'cancelled':
      case 'canceled':
        return 'canceled';
      default:
        return 'incoming';
    }
  }

  getActivityStatusLabel(activity: EditGroupActivity): string {
    switch (this.getActivityStatus(activity)) {
      case 'incoming':
        return 'Incoming';
      case 'completed':
        return 'Completed';
      case 'canceled':
        return 'Canceled';
    }
  }

  isActivityIncoming(activity: EditGroupActivity): boolean {
    return this.getActivityStatus(activity) === 'incoming';
  }

  getActivityDate(activity: EditGroupActivity): Date {
    const date = activity.startDateIso
      ? new Date(activity.startDateIso)
      : new Date(activity.date ?? `${activity.date} ${activity.time}`);
    return Number.isNaN(date.getTime()) ? new Date(0) : date;
  }

  getActivityEndDate(activity: EditGroupActivity): Date {
    if (activity.endDateIso) {
      const apiEndDate = new Date(activity.endDateIso);
      if (!Number.isNaN(apiEndDate.getTime())) {
        return apiEndDate;
      }
    }

    const endDate = new Date(this.getActivityDate(activity));
    endDate.setMinutes(endDate.getMinutes() + 120);
    return endDate;
  }

  isWeeklyActivity(activity: EditGroupActivity): boolean {
    return activity.recurrence === 'weekly';
  }

  getActivityRangeEndDate(activity: EditGroupActivity): Date {
    if (activity.endDateIso) {
      const apiEndDate = new Date(activity.endDateIso);
      if (!Number.isNaN(apiEndDate.getTime())) {
        return apiEndDate;
      }
    }

    const endDate = new Date(this.getActivityDate(activity));
    endDate.setDate(endDate.getDate() + 30);
    return endDate;
  }

  getActivityListTotal(): number {
    return this.selectedGroupDetail?.activitiesTotal ?? 0;
  }

  toggleActivitiesExpanded(status: GroupActivityStatusTab) {
    if (this.activeActivityStatus !== status) {
      this.activeActivityStatus = status;
      this.activitiesExpanded = false;
    } else {
      this.activitiesExpanded = !this.activitiesExpanded;
    }

    if (this.activitiesExpanded && this.selectedGroupDetail && this.selectedGroupRow) {
      const groupId = this.selectedGroupRow.id;
      const total = this.getActivityListTotal();
      this.groupsApi.getActivities(groupId, 1, total, status).subscribe((section) => {
        if (this.selectedGroupRow?.id === groupId && this.selectedGroupDetail) {
          this.selectedGroupDetail.activities = section.data;
          this.cdr.markForCheck();
        }
      });
    } else if (!this.activitiesExpanded) {
      this.loadActivityStatusPreview(status);
    }
    this.cdr.markForCheck();
  }

  onActivityStatusSelected(status: GroupActivityStatusTab) {
    if (this.activeActivityStatus === status) {
      return;
    }
    this.activeActivityStatus = status;
    this.activitiesExpanded = false;
    this.loadActivityStatusPreview(status);
    this.cdr.markForCheck();
  }

  private loadActivityStatusPreview(status: GroupActivityStatusTab) {
    if (!this.selectedGroupDetail || !this.selectedGroupRow) {
      return;
    }
    const groupId = this.selectedGroupRow.id;
    const alreadyLoaded = this.selectedGroupDetail.activities.some(
      (act) => this.toActivityStatusCountKey(act.status) === status,
    );
    if (alreadyLoaded) {
      return;
    }
    this.groupsApi.getActivities(groupId, 1, this.activityListPreviewCount, status).subscribe((section) => {
      if (this.selectedGroupRow?.id === groupId && this.selectedGroupDetail) {
        this.selectedGroupDetail.activities = section.data;
        this.cdr.markForCheck();
      }
    });
  }

  private toActivityStatusCountKey(status?: string): keyof ActivityStatusCounts {
    const normalizedStatus = status?.trim().toLowerCase();
    switch (normalizedStatus) {
      case 'scheduled':
        return 'incoming';
      case 'finished':
        return 'completed';
      case 'cancelled':
        return 'cancelled';
      default:
        return 'incoming';
    }
  }

  getEmptyActivityStatusCounts(): ActivityStatusCounts {
    return {
      incoming: 0,
      completed: 0,
      cancelled: 0,
    };
  }

  /**
   * Called with two different things, which the cast used to hide.
   *
   * The template passes the activity itself from the deleted list, and the
   * readonly-lists component passes the DOM event through its thumbnailError
   * output. The parameter said Event and an assertion covered the other half.
   *
   * An Event has no id, so the property is what tells them apart. The falsy
   * check is kept rather than a plain `'id' in source`: an activity whose id
   * is an empty string used to fall through to the DOM branch, and this pass
   * is about types, not about changing what the screen does.
   */
  onListThumbError(source: Event | EditGroupActivity) {
    if (!source) return;
    if ('id' in source && source.id) {
      source.thumbnailUrl = '';
      this.cdr.markForCheck();
      return;
    }

    const image = (source as Event).target as HTMLImageElement | null;
    if (!image) return;
    if (!image.src.includes(this.defaultCoverPhotoUrl)) {
      image.src = this.defaultCoverPhotoUrl;
      this.cdr.markForCheck();
      return;
    }
    image.style.display = 'none';
  }

  hasActivityThumbnailUrl(activity: EditGroupActivity): boolean {
    return !!activity?.thumbnailUrl?.trim();
  }

  // ─── Activity detail drawer computed getters (mirrors ActivityPreviewStateService) ───
  getActivityDetailInitials(name?: string): string {
    return this.getInitials(name);
  }

  getActivityDetailCreatedOn(): string {
    const a = this.activityDetail;
    const createdAt = a?.createdAtIso ? new Date(a.createdAtIso) : null;
    return createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt.toLocaleDateString('en-GB') : a?.date ?? '-';
  }

  getActivityDetailCoverPhotoSrc(): string {
    if (this.activityDetailCoverFailed || !this.activityDetail?.thumbnailUrl?.trim()) {
      return DEFAULT_GROUP_COVER_URL;
    }
    return this.activityDetail.thumbnailUrl;
  }

  getActivityDetailScheduleLabel(): string {
    const a = this.activityDetail;
    if (!a?.startDateIso) return '-';
    const weekday = formatWeekday(a.startDateIso);
    const startTime = formatTime(a.startDateIso);
    if (!weekday || !startTime) return '-';
    const prefix = a.recurrence === 'weekly' ? 'Every ' : '';
    return `${prefix}${weekday} at ${startTime}`;
  }

  getActivityDetailStartDate(): string {
    const iso = this.activityDetail?.startDateIso;
    if (!iso) return this.activityDetail?.date ?? '-';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? this.activityDetail?.date ?? '-'
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  getActivityDetailEndDate(): string {
    const iso = this.activityDetail?.endDateIso;
    if (!iso) return '-';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? '-'
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  getActivityDetailDurationLabel(): string {
    return formatDurationFromNumber(this.activityDetail?.durationSeconds) || '-';
  }

  getActivityDetailLevelLabel(): string {
    const level = this.activityDetailLevel;
    return level === 'all' ? 'All levels' : `Level ${level}`;
  }

  getActivityDetailHostEmail(): string {
    return this.activityDetail?.hostEmail ?? '';
  }

  getActivityDetailVisibilityText(): string {
    const activity = this.activityDetail;
    if (activity?.privacy === 'public') {
      return 'Everyone';
    }
    if (activity?.privacy === 'members_only') {
      return `Member of ${activity.groupName ?? ''}`.trim();
    }
    return 'Everyone';
  }
}
